import type { IAttachmentProps } from "./types";
import { Download, File, Trash2 } from "lucide-react";
import { Typography } from "../Typography";
import { formatFileSize } from "../../utils/formatFileSize";
import { IconButton } from "../Button/IconButton";
import { useCallback, useEffect, useState } from "react";
import { clsx } from "clsx";
import { Message } from "../Message";
import { TruncatedContent } from "../TruncatedContent";
import { FileUtils } from "../../../utils/FileUtils";

export const Attachment = (props: IAttachmentProps) => {
  const {
    file,
    onRemove,
    onDownload,
    invalid,
    message,
    showFileSize = true,
    name,
    size,
    downloading,
    deleting,
    variant = "layer1",
  } = props;
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);

  const rootClass = clsx(
    "nuco-attachment",
    `nuco-attachment--${variant}`,
    invalid && "nuco-attachment--invalid",
  );

  useEffect(() => {
    if (file && FileUtils.isImageFile(file)) {
      const previewUrl = FileUtils.createImagePreviewUrl(file);
      setImagePreviewUrl(previewUrl);

      return () => {
        URL.revokeObjectURL(previewUrl);
      };
    }

    setImagePreviewUrl(null);
  }, [file]);

  const handleRemove = useCallback(() => {
    if (onRemove) {
      onRemove(file);
    }
  }, [file, onRemove]);

  const handleDownload = useCallback(() => {
    if (onDownload) {
      onDownload(file);
    }
  }, [file, onDownload]);

  return (
    <div className={rootClass}>
      <div className="nuco-attachment__content">
        <div className="nuco-attachment__icon">
          {imagePreviewUrl ? (
            <img
              src={imagePreviewUrl}
              alt={name || file?.name || "Image preview"}
              className="nuco-attachment__image-preview"
            />
          ) : (
            <File />
          )}
        </div>
        <div className="nuco-attachment__details">
          <TruncatedContent
            className="nuco-attachment__name nuco-typography--body"
            content={name || file?.name || "-"}
          />
          {showFileSize && (
            <Typography
              as="div"
              className="nuco-attachment__size"
              text={size ? size : file ? formatFileSize(file.size) : "-"}
            />
          )}
        </div>
        {(onRemove || onDownload) && (
          <div className="nuco-attachment__actions">
            {onDownload && (
              <IconButton
                size="md"
                color={variant}
                content={<Download />}
                onClick={handleDownload}
                loading={downloading}
              />
            )}
            {onRemove && (
              <IconButton
                size="md"
                color={variant}
                content={<Trash2 />}
                onClick={handleRemove}
                loading={deleting}
                aria-label="Remove file"
              />
            )}
          </div>
        )}
      </div>
      {message && <Message text={message} variant="red" />}
    </div>
  );
};
